import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Save, CheckCircle2, XCircle, MinusCircle, MessageSquare, Trash2, FileWarning, ChevronDown } from 'lucide-react'
import { checklistApi } from '../services/checklistApi'
import { issueApi } from '../services/issueApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { StatusBadge } from '../components/common/StatusBadge'
import { ProgressBar } from '../components/common/ProgressBar'
import type { Checklist, ChecklistItem, IssueCategory, IssueType, PunchItemPriority } from '../types'

interface ItemState {
  response: string | null
  passed: boolean | null
  notes: string
  dirty: boolean
}

export function ChecklistDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [checklist, setChecklist] = useState<Checklist | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [itemStates, setItemStates] = useState<Map<string, ItemState>>(new Map())
  const [saving, setSaving] = useState<Set<string>>(new Set())
  const [completing, setCompleting] = useState(false)
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  const [deletingItem, setDeletingItem] = useState<string | null>(null)
  const [crossLinkItem, setCrossLinkItem] = useState<ChecklistItem | null>(null)
  const [crossLinkType, setCrossLinkType] = useState<'issue' | null>(null)
  const [crossLinkSaving, setCrossLinkSaving] = useState(false)
  const [crossLinkSuccess, setCrossLinkSuccess] = useState<string | null>(null)
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [showSectionNav, setShowSectionNav] = useState(false)

  // Section completion stats
  const sectionStats = useMemo(() => {
    const stats: Record<string, { total: number; done: number }> = {}
    if (!checklist) return stats
    const sectionNames = Array.from(new Set(checklist.items.map((i) => i.section)))
    for (const section of sectionNames) {
      const sectionItems = checklist.items.filter(i => i.section === section)
      const done = sectionItems.filter(i => {
        const s = itemStates.get(i.id)
        return s && (s.response !== null || s.passed !== null)
      }).length
      stats[section] = { total: sectionItems.length, done }
    }
    return stats
  }, [checklist, itemStates])

  const loadChecklist = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await checklistApi.getById(id)
      setChecklist(data)
      const states = new Map<string, ItemState>()
      data.items.forEach((item) => {
        states.set(item.id, {
          response: item.response,
          passed: item.passed,
          notes: item.notes,
          dirty: false,
        })
      })
      setItemStates(states)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load checklist')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { loadChecklist() }, [loadChecklist])

  function updateItemState(itemId: string, updates: Partial<ItemState>) {
    setItemStates((prev) => {
      const next = new Map(prev)
      const current = next.get(itemId)
      if (current) {
        next.set(itemId, { ...current, ...updates, dirty: true })
      }
      return next
    })
  }

  // Auto-save: update state and immediately persist to API
  function respondToItem(item: ChecklistItem, updates: Partial<ItemState>) {
    const current = itemStates.get(item.id)
    if (!current) return
    const merged = { ...current, ...updates }
    // Update local state immediately
    setItemStates((prev) => {
      const next = new Map(prev)
      next.set(item.id, { ...merged, dirty: false })
      return next
    })
    // Persist to API
    if (!id) return
    setSaving((prev) => new Set(prev).add(item.id))
    checklistApi.updateItem(id, item.id, {
      response: merged.response,
      passed: merged.passed,
      notes: merged.notes,
    }).catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to save item')
      // Mark dirty again on failure
      setItemStates((prev) => {
        const next = new Map(prev)
        const cur = next.get(item.id)
        if (cur) next.set(item.id, { ...cur, dirty: true })
        return next
      })
    }).finally(() => {
      setSaving((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    })
  }

  async function saveItem(item: ChecklistItem) {
    if (!id) return
    const state = itemStates.get(item.id)
    if (!state || !state.dirty) return

    setSaving((prev) => new Set(prev).add(item.id))
    try {
      await checklistApi.updateItem(id, item.id, {
        response: state.response,
        passed: state.passed,
        notes: state.notes,
      })
      setItemStates((prev) => {
        const next = new Map(prev)
        const current = next.get(item.id)
        if (current) next.set(item.id, { ...current, dirty: false })
        return next
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item')
    } finally {
      setSaving((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
    }
  }

  async function saveAllDirty() {
    if (!checklist) return
    const dirtyItems = checklist.items.filter((item) => itemStates.get(item.id)?.dirty)
    for (const item of dirtyItems) {
      await saveItem(item)
    }
  }

  async function handleComplete() {
    if (!id) return
    try {
      setCompleting(true)
      await saveAllDirty()
      await checklistApi.complete(id)
      await loadChecklist()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete checklist')
    } finally {
      setCompleting(false)
    }
  }

  function toggleNotes(itemId: string) {
    setExpandedNotes((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  async function deleteItem(itemId: string) {
    if (!id) return
    try {
      setDeletingItem(itemId)
      await checklistApi.deleteItem(id, itemId)
      await loadChecklist()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item')
    } finally {
      setDeletingItem(null)
    }
  }

  async function createIssueFromItem(item: ChecklistItem) {
    if (!checklist) return
    setCrossLinkSaving(true)
    try {
      const created = await issueApi.create({
        projectId: Number(checklist.projectId),
        systemId: checklist.systemId ? Number(checklist.systemId) : undefined,
        equipmentId: checklist.equipmentId ? Number(checklist.equipmentId) : undefined,
        checklistId: Number(checklist.id),
        title: `Failed: ${item.question}`,
        description: `Checklist "${checklist.name}" item #${item.sortOrder} failed.\n\nQuestion: ${item.question}${itemStates.get(item.id)?.notes ? `\nNotes: ${itemStates.get(item.id)?.notes}` : ''}`,
        category: 'Commissioning' as IssueCategory,
        type: 'Deficiency' as IssueType,
        priority: 'Medium' as PunchItemPriority,
      })
      setCrossLinkSuccess(`Issue created: ${created.issueNumber} — ${created.title}`)
      setCrossLinkItem(null)
      setCrossLinkType(null)
      setTimeout(() => setCrossLinkSuccess(null), 4000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create issue')
    } finally {
      setCrossLinkSaving(false)
    }
  }

  if (loading) return <LoadingSpinner message="Loading checklist..." />
  if (error && !checklist) {
    return (
      <div className="page-container">
        <div className="info-card danger"><p style={{ margin: 0 }}>{error}</p></div>
      </div>
    )
  }
  if (!checklist) return null

  const sections = Array.from(new Set(checklist.items.map((i) => i.section)))
  const isComplete = checklist.status === 'Complete'

  const completedCount = Array.from(itemStates.values()).filter((s) => s.response !== null || s.passed !== null).length
  const totalCount = checklist.items.length
  const completion = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  const hasDirty = Array.from(itemStates.values()).some((s) => s.dirty)

  function scrollToSection(section: string) {
    sectionRefs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setShowSectionNav(false)
  }

  return (
    <div className="page-container animate-fade-in">
      <div className="breadcrumbs">
        <Link to="/checklists">Checklists</Link>
        <span className="breadcrumbs-separator">/</span>
        <span className="breadcrumbs-current">{checklist.name}</span>
      </div>

      {/* Sticky header with progress */}
      <div className="checklist-sticky-header">
        <div className="d-flex align-center gap-3" style={{ flex: 1, minWidth: 0 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="d-flex align-center gap-2">
              <h1 style={{ margin: 0, fontSize: 'var(--font-size-lg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{checklist.name}</h1>
              <span className="badge badge-primary-light">{checklist.type || 'PFC'}</span>
              <StatusBadge status={checklist.status} />
            </div>
            <div className="d-flex align-center gap-3 mt-1">
              <div style={{ flex: 1, maxWidth: 200 }}>
                <ProgressBar value={completion} />
              </div>
              <span className="text-xs text-muted">{completedCount}/{totalCount}</span>
            </div>
          </div>
        </div>
        <div className="d-flex gap-2 align-center" style={{ flexShrink: 0 }}>
          {sections.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowSectionNav(!showSectionNav)}>
                Sections <ChevronDown size={14} />
              </button>
              {showSectionNav && (
                <div className="checklist-section-dropdown">
                  {sections.map(section => {
                    const s = sectionStats[section]
                    const allDone = s && s.done === s.total
                    return (
                      <button
                        key={section}
                        className="checklist-section-dropdown-item"
                        onClick={() => scrollToSection(section)}
                      >
                        <span style={{ color: allDone ? 'var(--accent-green)' : 'var(--text-primary)' }}>
                          {allDone && <CheckCircle2 size={12} style={{ marginRight: 4, verticalAlign: -1 }} />}
                          {section}
                        </span>
                        <span className="text-xs text-muted">{s?.done}/{s?.total}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          {hasDirty && !isComplete && (
            <button className="btn btn-secondary btn-sm" onClick={saveAllDirty}>
              <Save size={14} /> Save
            </button>
          )}
          {!isComplete && (
            <button className="btn btn-success btn-sm" onClick={handleComplete} disabled={completing}>
              <CheckCircle2 size={14} /> {completing ? '...' : 'Complete'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {sections.map((section) => {
        const sectionItems = checklist.items.filter((i) => i.section === section)
        const ss = sectionStats[section]
        return (
          <div key={section} className="checklist-section" ref={el => { sectionRefs.current[section] = el }}>
            <div className="d-flex align-center justify-between">
              <h3 className="checklist-section-title">{section}</h3>
              {ss && <span className="text-xs text-muted">{ss.done}/{ss.total}</span>}
            </div>
            <div className="card">
              <div className="card-body p-0">
                {sectionItems.map((item) => {
                  const state = itemStates.get(item.id)
                  if (!state) return null
                  const isSaving = saving.has(item.id)
                  const notesOpen = expandedNotes.has(item.id)

                  return (
                    <div key={item.id} className={`checklist-item${state.dirty ? ' dirty' : ''}${state.response === 'NA' ? ' na' : state.passed === true ? ' passed' : state.passed === false ? ' failed' : ''}`}>
                      <div className="checklist-item-main">
                        <div className="checklist-item-question">
                          <span className="checklist-item-number">#{item.sortOrder}</span>
                          <span className={state.response === 'NA' ? 'text-muted' : ''}>{item.question}</span>
                          {item.required && state.response !== 'NA' && <span className="text-danger text-xs">*</span>}
                        </div>
                        <div className="checklist-item-response">
                          {(item.responseType === 'PassFail') && (
                            <div className="response-toggle">
                              <button
                                type="button"
                                className={`response-btn pass${state.passed === true && state.response !== 'NA' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { passed: true, response: 'Pass' })}
                                disabled={isComplete}
                              >
                                <CheckCircle2 size={16} /> Pass
                              </button>
                              <button
                                type="button"
                                className={`response-btn fail${state.passed === false && state.response !== 'NA' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { passed: false, response: 'Fail' })}
                                disabled={isComplete}
                              >
                                <XCircle size={16} /> Fail
                              </button>
                              <button
                                type="button"
                                className={`response-btn na${state.response === 'NA' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { passed: null, response: 'NA' })}
                                disabled={isComplete}
                              >
                                <MinusCircle size={16} /> N/A
                              </button>
                            </div>
                          )}
                          {(item.responseType === 'YesNo') && (
                            <div className="response-toggle">
                              <button
                                type="button"
                                className={`response-btn pass${state.response === 'Yes' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { response: 'Yes', passed: true })}
                                disabled={isComplete}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                className={`response-btn fail${state.response === 'No' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { response: 'No', passed: false })}
                                disabled={isComplete}
                              >
                                No
                              </button>
                              <button
                                type="button"
                                className={`response-btn na${state.response === 'NA' ? ' active' : ''}`}
                                onClick={() => respondToItem(item, { passed: null, response: 'NA' })}
                                disabled={isComplete}
                              >
                                N/A
                              </button>
                            </div>
                          )}
                          {item.responseType === 'Text' && (
                            <input
                              type="text"
                              className="form-input form-input-sm"
                              value={state.response || ''}
                              onChange={(e) => updateItemState(item.id, { response: e.target.value })}
                              placeholder="Enter response..."
                              disabled={isComplete}
                              style={{ maxWidth: '300px' }}
                            />
                          )}
                          {item.responseType === 'Number' && (
                            <input
                              type="number"
                              className="form-input form-input-sm"
                              value={state.response || ''}
                              onChange={(e) => updateItemState(item.id, { response: e.target.value })}
                              placeholder="Enter value..."
                              disabled={isComplete}
                              style={{ maxWidth: '160px' }}
                            />
                          )}
                          {item.responseType === 'Select' && (
                            <select
                              className="form-select form-select-sm"
                              value={state.response || ''}
                              onChange={(e) => updateItemState(item.id, { response: e.target.value })}
                              disabled={isComplete}
                              style={{ maxWidth: '240px' }}
                            >
                              <option value="">Select...</option>
                              {item.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          )}
                          {state.passed === false && state.response !== 'NA' && !isComplete && (
                            <div className="d-flex gap-1" style={{ marginLeft: '4px' }}>
                              <button
                                type="button"
                                className="btn btn-xs btn-ghost"
                                style={{ color: 'var(--accent-red)', fontSize: 'var(--font-size-xs)' }}
                                onClick={() => { setCrossLinkItem(item); setCrossLinkType('issue') }}
                                title="Create issue from this failure"
                              >
                                <FileWarning size={12} /> Issue
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`btn btn-ghost btn-xs btn-icon${notesOpen ? ' text-accent' : ''}`}
                            onClick={() => toggleNotes(item.id)}
                            title="Notes"
                          >
                            <MessageSquare size={14} />
                          </button>
                          {state.dirty && !isComplete && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => saveItem(item)}
                              disabled={isSaving}
                            >
                              {isSaving ? '...' : <Save size={14} />}
                            </button>
                          )}
                          {!isComplete && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs btn-icon text-danger"
                              onClick={() => { if (confirm('Remove this item from the checklist?')) deleteItem(item.id) }}
                              disabled={deletingItem === item.id}
                              title="Remove item"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                      {notesOpen && (
                        <div className="checklist-item-notes">
                          <textarea
                            className="form-textarea"
                            value={state.notes}
                            onChange={(e) => updateItemState(item.id, { notes: e.target.value })}
                            placeholder="Add notes..."
                            disabled={isComplete}
                            rows={2}
                            style={{ minHeight: '60px' }}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )
      })}

      {crossLinkSuccess && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000,
          background: 'var(--accent-green)', color: 'white', padding: '12px 20px',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          fontSize: 'var(--font-size-sm)', fontWeight: 500, maxWidth: '400px',
          animation: 'fadeIn 0.3s ease',
        }}>
          <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          {crossLinkSuccess}
        </div>
      )}

      {crossLinkItem && crossLinkType && createPortal(
        <div className="modal-overlay" onClick={() => { setCrossLinkItem(null); setCrossLinkType(null) }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Create Issue from Failed Item</h2>
              <button className="modal-close" onClick={() => { setCrossLinkItem(null); setCrossLinkType(null) }}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="info-card warning mb-4">
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                  <strong>Item #{crossLinkItem.sortOrder}:</strong> {crossLinkItem.question}
                </p>
              </div>
              <p className="text-secondary-color text-sm">
                An issue will be created and linked to this checklist with category "Commissioning" and type "Deficiency".
              </p>
              {itemStates.get(crossLinkItem.id)?.notes && (
                <div className="mt-3">
                  <p className="text-sm text-muted mb-1">Notes will be included:</p>
                  <p className="text-sm" style={{ fontStyle: 'italic' }}>{itemStates.get(crossLinkItem.id)?.notes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setCrossLinkItem(null); setCrossLinkType(null) }}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={crossLinkSaving}
                onClick={() => createIssueFromItem(crossLinkItem)}
              >
                {crossLinkSaving ? 'Creating...' : 'Create Issue'}
              </button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  )
}
